import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchWithCsrf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useConfirm } from "@/hooks/useConfirm";
import type { User } from "@/types/auth";
import type { ApiSuccess, ApiError } from "@/types/api";

const ADMIN_USERS_QUERY_KEY = ["admin", "users"] as const;

interface UsersResponse {
  users: User[];
}

export default function AdminUsers() {
  const queryClient = useQueryClient();
  const { confirmProps, confirm } = useConfirm();

  const { data: users, isLoading } = useQuery<User[]>({
    queryKey: ADMIN_USERS_QUERY_KEY,
    queryFn: async () => {
      const result = await fetchWithCsrf<ApiSuccess<UsersResponse>>(
        "/api/admin/users"
      );
      return result.data.users;
    },
  });

  const updateUser = useMutation<
    ApiSuccess<{ user: User }>,
    ApiError,
    { id: string; role: string }
  >({
    mutationFn: async ({ id, role }) => {
      return fetchWithCsrf(`/api/admin/users/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ user: { role } }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });

  const deleteUser = useMutation<void, ApiError, string>({
    mutationFn: async (id) => {
      await fetchWithCsrf(`/api/admin/users/${id}`, { method: "DELETE" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });

  const restoreUser = useMutation<ApiSuccess<{ user: User }>, ApiError, string>(
    {
      mutationFn: async (id) => {
        return fetchWithCsrf(`/api/admin/users/${id}/restore`, {
          method: "POST",
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
      },
    }
  );

  const handleToggleRole = (user: User) => {
    const newRole = user.role === "admin" ? "user" : "admin";
    confirm({
      title: "Change Role",
      description: `Are you sure you want to change ${user.name}'s role to ${newRole}?`,
      confirmLabel: newRole === "admin" ? "Make Admin" : "Make User",
      onConfirm: () => updateUser.mutate({ id: user.id, role: newRole }),
    });
  };

  const handleDelete = (user: User) => {
    confirm({
      title: "Delete User",
      description: `Are you sure you want to delete ${user.name}?`,
      confirmLabel: "Delete",
      variant: "destructive",
      onConfirm: () => deleteUser.mutate(user.id),
    });
  };

  const handleRestore = (user: User) => {
    confirm({
      title: "Restore User",
      description: `Are you sure you want to restore ${user.name}?`,
      confirmLabel: "Restore",
      onConfirm: () => restoreUser.mutate(user.id),
    });
  };

  return (
    <div className="max-w-4xl">
      <h1 className="text-xl font-medium uppercase tracking-wider mb-8">User Management</h1>

      {isLoading ? (
        <p className="text-muted-foreground">Loading users...</p>
      ) : users && users.length > 0 ? (
        <div className="bg-[#191919] border border-[#616161]/20 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#191919]">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Name
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Email
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Role
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Status
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#616161]/20">
              {users.map((user) => (
                <tr
                  key={user.id}
                  className={user.discardedAt ? "opacity-50" : ""}
                >
                  <td className="px-4 py-3 text-sm">{user.name}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground font-mono">
                    {user.email}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium uppercase tracking-wider ${
                        user.role === "admin"
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {user.discardedAt ? (
                      <span className="text-destructive text-xs font-medium uppercase tracking-wider">Deleted</span>
                    ) : (
                      <span className="text-affirmative text-xs font-medium uppercase tracking-wider">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {user.discardedAt ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestore(user)}
                          disabled={restoreUser.isPending}
                        >
                          Restore
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleRole(user)}
                            disabled={updateUser.isPending}
                          >
                            {user.role === "admin" ? "Make User" : "Make Admin"}
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(user)}
                            disabled={deleteUser.isPending}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-muted-foreground">No users found.</p>
      )}

      <ConfirmDialog {...confirmProps} />
    </div>
  );
}
